/// Deployment target the build points at.
///
/// Mirrors mobile/lib/app/env/app_env.dart. Unlike Flutter's compile-time
/// `String.fromEnvironment`, the web build reads this at runtime from
/// `window.__APP_CONFIG__`, which docker-entrypoint.sh writes into config.js.
/// One image therefore promotes across every environment without a rebuild.

export const appEnvironments = ['local', 'dev', 'qa', 'prod'] as const;

export type AppEnvironment = (typeof appEnvironments)[number];

export interface AppEnv {
  environment: AppEnvironment;
  apiUrl: string;
  sentryDsn: string;
  /** Web OAuth client used by Google Identity Services. Empty hides the button. */
  googleClientId: string;
  /** When true, ad slots may render (prod). Script loads when adsenseClientId is set. */
  adsEnabled: boolean;
  /** AdSense publisher id (ca-pub-…). Empty skips the adsbygoogle.js tag. */
  adsenseClientId: string;
  isLocal: boolean;
  isProd: boolean;
  /** Gates developer-only UI. Local counts as dev. */
  isDev: boolean;
  isSentryEnabled: boolean;
  tracesSampleRate: number;
  /** Matches package.json; used for remote min-version checks. */
  version: string;
}

interface RuntimeConfig {
  envName?: string;
  apiUrl?: string;
  sentryDsn?: string;
  googleClientId?: string;
  adsEnabled?: boolean;
  adsenseClientId?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: RuntimeConfig;
  }
}

function parseEnvironment(value: string | undefined): AppEnvironment {
  return appEnvironments.find((name) => name === value) ?? 'local';
}

function readRuntimeConfig(): RuntimeConfig {
  return (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};
}

function build(): AppEnv {
  const raw = readRuntimeConfig();
  const environment = parseEnvironment(raw.envName);
  const sentryDsn = raw.sentryDsn ?? '';
  const isProd = environment === 'prod';

  return {
    environment,
    apiUrl: (raw.apiUrl ?? 'http://localhost:8080').replace(/\/+$/, ''),
    sentryDsn,
    googleClientId: raw.googleClientId ?? '',
    adsEnabled: raw.adsEnabled === true,
    adsenseClientId: raw.adsenseClientId ?? '',
    isLocal: environment === 'local',
    isProd,
    isDev: environment === 'dev' || environment === 'local',
    // Local traffic is noise, so it is never reported.
    isSentryEnabled: sentryDsn.length > 0 && (isProd || environment === 'qa'),
    tracesSampleRate: isProd ? 0.2 : 1.0,
    version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0',
  };
}

export const appEnv: AppEnv = build();
